import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelText as _vModelText, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "rank-details-page" }
const _hoisted_2 = { class: "info-display-container" }
const _hoisted_3 = { class: "display-item" }
const _hoisted_4 = ["value"]
const _hoisted_5 = { class: "display-item" }
const _hoisted_6 = ["value"]
const _hoisted_7 = { class: "display-item" }
const _hoisted_8 = {
  key: 0,
  class: "loading-container"
}
const _hoisted_9 = {
  key: 1,
  class: "error-message"
}
const _hoisted_10 = {
  key: 2,
  class: "table-container"
}
const _hoisted_11 = { class: "rank-details-table" }

import { useRouter } from 'vue-router';
import { ref, computed, onMounted } from 'vue';
import api from '@/servers/api';
import { useUserStore } from '@/store';
import { message } from 'ant-design-vue';
import { getStudentExamApi } from '@/servers/api/student';
import { getTotalScore, getTotalClassRank, getTotalSchoolRank, getSubjectScore, getSubjectClassRank, getSubjectSchoolRank } from '@/utils/subjects';


export default /*@__PURE__*/_defineComponent({
  __name: 'RankDetailsPage',
  setup(__props) {

const router = useRouter();
const userStore = useUserStore();

// State
const showRankDetailsTable = ref(false);
const isLoading = ref(false);
const errorMessage = ref('');

// Form data
const selectedClassId = ref('');
const classes = ref<API.Classes[]>([]);

const selectedExamId = ref('');
const exams = ref<{id: string; name: string}[]>([]);

const studentName = ref('');
const currentStudentId = ref('');
const studentInfo = ref<any>(null);

// Data storage - 存储完整班级数据（用于精准定位目标学生）
const fullClassStudents = ref<any[]>([]);
const top3Students = ref<any[]>([]);

// 动态科目列表（基于学生选科）
const dynamicSubjects = computed(() => {
  const subjects: { name: string; key: string }[] = [];
  
  if (!studentInfo.value?.subject_selection) return subjects;
  
  const subjectMap = {
    '物': { name: '物理', key: 'physics' },
    '化': { name: '化学', key: 'chemistry' },
    '生': { name: '生物', key: 'biology' },
    '史': { name: '历史', key: 'history' },
    '地': { name: '地理', key: 'geography' },
    '政': { name: '政治', key: 'politics' }
  };
  
  for (const key in subjectMap) {
    if (studentInfo.value.subject_selection.includes(key)) {
      subjects.push(subjectMap[key as keyof typeof subjectMap]);
    }
  }
  
  return subjects;
});

// 姓名脱敏函数
const maskName = (name: string, isCurrentUser: boolean) => {
  // 如果是当前用户，不脱敏
  if (isCurrentUser) {
    return name;
  }
  console.log(isCurrentUser)
  // 如果姓名为空，返回空字符串
  if (!name) {
    return '';
  }
  
  // 根据姓名长度进行脱敏处理
  if (name.length === 2) {
    // 双字姓名显示为X*
    return name.charAt(0) + '*';
  } else {
    // 三字及以上姓名显示为X**X（保留首尾字，中间用*替代）
    return name.charAt(0) + '**'
  }
};

// 初始化数据
onMounted(async () => {
  const userInfo = userStore.userInfo;
  
  if (!userInfo) {
    errorMessage.value = '用户信息获取失败，请重新登录';
    return;
  }

  if (userInfo.student) {
    await initStudentInfo(userInfo);
  } else {
    errorMessage.value = '请以学生身份登录查看';
  }
});

// 初始化学生信息
const initStudentInfo = async (userInfo: any) => {
  try {
    isLoading.value = true;
    const studentInfoData = userInfo.student;
    
    if (!studentInfoData) {
      message.warning('请先完善学生信息');
      return;
    }

    studentName.value = studentInfoData.name || '';
    currentStudentId.value = studentInfoData.uid || '';
    studentInfo.value = studentInfoData;
    
    const studentClassId = studentInfoData.class_id;
    if (studentClassId) {
      await fetchClassDetails(studentClassId.toString());
      await fetchExamsByStudent(userInfo.role);
    } else {
      message.warning('未找到学生所在班级信息');
    }
    
    // 默认选中最新考试
    if (exams.value.length > 0) {
      const sortedExams = [...exams.value].sort((a, b) => Number(b.id) - Number(a.id));
      selectedExamId.value = sortedExams[0].id;
    }
  } catch (err) {
    message.error('获取当前学生信息失败，请重试');
  } finally {
    isLoading.value = false;
  }
};

// 获取学生考试列表
const fetchExamsByStudent = async (studentUid: string) => {
  try {
    isLoading.value = true;
    const response = await getStudentExamApi({ student_uid: studentUid });
    
    if (response.code === 200) {
      exams.value = response.data.map((item: any[]) => {
        if (Array.isArray(item) && item[0] && item[0].id && item[0].name) {
          return {
            id: item[0].id.toString(),
            name: item[0].name
          };
        }
        return null;
      }).filter(Boolean);
    } else {
      message.error(response.msg || '获取考试列表失败');
      exams.value = [];
    }
  } catch (err) {
    message.error('获取考试列表失败，请刷新页面重试');
  } finally {
    isLoading.value = false;
  }
};

// 获取班级详情
const fetchClassDetails = async (classId: string) => {
  try {
    // 修复类型错误：getClassesApi 不接受 id 参数，应该使用 class_id 参数
    const response = await api.classes.getClassesApi({ school_id: classId });
    if (response.data && response.data.length > 0) {
      classes.value = response.data;
      selectedClassId.value = classId;
    } else {
      // 修复类型错误：为 Classes 对象提供所有必需的属性
      classes.value = [{
        id: parseInt(classId),
        name: `班级 ${classId}`,
        header: '', // 班主任ID
        school_id: classId, // 学校ID
        subject_selection: '' // 选科
      }];
      selectedClassId.value = classId;
    }
  } catch (err) {
    // 修复类型错误：为 Classes 对象提供所有必需的属性
    classes.value = [{
      id: parseInt(classId),
      name: `班级 ${classId}`,
      header: '', // 班主任ID
      school_id: classId, // 学校ID
      subject_selection: '' // 选科
    }];
    selectedClassId.value = classId;
  }
};

// 修改查询名次详情方法
const queryRankDetails = async () => {
  // 基础校验保持不变
  if (!selectedClassId.value) {
    errorMessage.value = '请选择班级';
    return;
  }
  if (!selectedExamId.value) {
    errorMessage.value = '请选择考试';
    return;
  }
  if (!studentName.value.trim()) {
    errorMessage.value = '请输入姓名';
    return;
  }

  try {
    isLoading.value = true;
    errorMessage.value = '';
    showRankDetailsTable.value = false;

    // 1. 获取目标学生成绩
    const targetRes = await api.grade.getGradeApi({
      student_id: parseInt(currentStudentId.value),
      exam_id: parseInt(selectedExamId.value),
    });

    if (!targetRes.data || targetRes.data.length === 0) {
      errorMessage.value = '未找到该学生的成绩信息';
      return;
    }
    const targetStudentData = transformGradeToStudent(targetRes.data[0]);

    // 2. 获取前三名学生
    const top3Res = await api.grade.getGradeApi({
      class_id: parseInt(selectedClassId.value),
      exam_id: parseInt(selectedExamId.value),
      size: 3
      // 移除不支持的 sort 参数
    });
    top3Students.value = (top3Res.data || []).map(transformGradeToStudent);

    // 3. 使用新接口获取前后10名学生
    const rankRes = await api.grade.getStudentGradeRank({
      student_id: currentStudentId.value,
      selected_exam_id: selectedExamId.value,
    });

    // 4. 处理接口返回的前后10名数据
    const aroundStudents = (rankRes.data?.around || []).map(transformGradeToStudent);
    
    // 5. 合并数据并去重
    fullClassStudents.value = aroundStudents;
    
    // 确保目标学生在列表中
    const isTargetInList = aroundStudents.some(
      (s: any) => s.studentId === currentStudentId.value
    );
    if (!isTargetInList) {
      fullClassStudents.value.push(targetStudentData);
    }

    showRankDetailsTable.value = true;
  } catch (err) {
    message.error('查询失败，请稍后重试');
  } finally {
    isLoading.value = false;
  }
};

// 转换成绩数据为学生格式
const transformGradeToStudent = (grade: any) => {
  const baseData: any = {
    studentId: grade.student_id,
    name: grade.student_name || '',
    totalScore: getTotalScore(grade),
    classRank: getTotalClassRank(grade),
    overallRank: getTotalSchoolRank(grade),
    chineseScore: getSubjectScore(grade, '语文'),
    chineseClassRank: getSubjectClassRank(grade, '语文'),
    chineseOverallRank: getSubjectSchoolRank(grade, '语文'),
    mathScore: getSubjectScore(grade, '数学'),
    mathClassRank: getSubjectClassRank(grade, '数学'),
    mathOverallRank: getSubjectSchoolRank(grade, '数学'),
    englishScore: getSubjectScore(grade, '英语'),
    englishClassRank: getSubjectClassRank(grade, '英语'),
    englishOverallRank: getSubjectSchoolRank(grade, '英语')
  };

  // 动态添加选考科目数据
  dynamicSubjects.value.forEach(subject => {
    const subjectKeyMap: Record<string, string> = {
      'physics': '物理',
      'chemistry': '化学',
      'biology': '生物',
      'history': '历史',
      'geography': '地理',
      'politics': '政治'
    };

    const cnName = subjectKeyMap[subject.key];
    if (cnName) {
      baseData[`${subject.key}Score`] = getSubjectScore(grade, cnName);
      baseData[`${subject.key}ClassRank`] = getSubjectClassRank(grade, cnName);
      baseData[`${subject.key}OverallRank`] = getSubjectSchoolRank(grade, cnName);
    }
  });

  return baseData;
};

// 修改displayedStudents计算属性（简化版本）
const displayedStudents = computed(() => {
  const finalList: any[] = [];
  const addedStudentIds = new Set<string>();

  // 添加前三名
  top3Students.value.forEach(student => {
    if (student.studentId && !addedStudentIds.has(student.studentId)) {
      // 对前三名学生也进行脱敏处理
      const isCurrentUser = student.studentId == currentStudentId.value;
      student.name = maskName(student.name, isCurrentUser);
      finalList.push(student);
      addedStudentIds.add(student.studentId);
    }
  });

  // 添加前后10名学生
  fullClassStudents.value.forEach(student => {
    if (student.studentId && !addedStudentIds.has(student.studentId)) {
      // 对学生姓名进行脱敏处理
      const isCurrentUser = student.studentId === currentStudentId.value;
      student.name = maskName(student.name, isCurrentUser);
      finalList.push(student);
      addedStudentIds.add(student.studentId);
    }
  });

  // 按班级排名排序
  return finalList.sort((a, b) => a.classRank - b.classRank);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[23] || (_cache[23] = _createElementVNode("h2", { class: "page-title" }, "名次详情", -1)),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[3] || (_cache[3] = _createElementVNode("span", { class: "label" }, "班级", -1)),
        _withDirectives(_createElementVNode("select", {
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedClassId).value = $event)),
          class: "value-select",
          disabled: ""
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(classes.value, (cls) => {
            return (_openBlock(), _createElementBlock("option", {
              key: cls.id,
              value: cls.id
            }, _toDisplayString(cls.name), 9, _hoisted_4))
          }), 128))
        ], 512), [
          [_vModelSelect, selectedClassId.value]
        ])
      ]),
      _createElementVNode("div", _hoisted_5, [
        _cache[5] || (_cache[5] = _createElementVNode("span", { class: "label" }, "考试", -1)),
        _withDirectives(_createElementVNode("select", {
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedExamId).value = $event)),
          class: "value-select"
        }, [
          _cache[4] || (_cache[4] = _createElementVNode("option", { value: "" }, "请选择考试", -1)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(exams.value, (exam) => {
            return (_openBlock(), _createElementBlock("option", {
              key: exam.id,
              value: exam.id
            }, _toDisplayString(exam.name), 9, _hoisted_6))
          }), 128))
        ], 512), [
          [_vModelSelect, selectedExamId.value]
        ])
      ]),
      _createElementVNode("div", _hoisted_7, [
        _cache[6] || (_cache[6] = _createElementVNode("span", { class: "label" }, "姓名", -1)),
        _withDirectives(_createElementVNode("input", {
          type: "text",
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((studentName).value = $event)),
          class: "value-input",
          placeholder: "请输入姓名",
          readonly: ""
        }, null, 512), [
          [_vModelText, studentName.value]
        ])
      ]),
      _cache[7] || (_cache[7] = _createElementVNode("p", { class: "description red-text" }, " 说明：成绩展示为班级前三名和以本学生为基准前后各10位同学的成绩（含本人）。 ", -1)),
      _createElementVNode("button", {
        class: "query-button",
        onClick: queryRankDetails
      }, "查询"),
      _cache[8] || (_cache[8] = _createElementVNode("p", { class: "info-hint" }, [
        _createElementVNode("span", { class: "info-icon" }, "i"),
        _createTextVNode(" 表格可向左滑动查看信息 ")
      ], -1))
    ]),
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [...(_cache[9] || (_cache[9] = [
          _createElementVNode("div", { class: "loading-spinner" }, null, -1),
          _createElementVNode("p", null, "加载中...", -1)
        ]))]))
      : _createCommentVNode("", true),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(errorMessage.value), 1))
      : _createCommentVNode("", true),
    (showRankDetailsTable.value && !isLoading.value && !errorMessage.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
          _createElementVNode("table", _hoisted_11, [
            _createElementVNode("thead", null, [
              _createElementVNode("tr", null, [
                _cache[10] || (_cache[10] = _createElementVNode("th", null, "姓名", -1)),
                _cache[11] || (_cache[11] = _createElementVNode("th", null, "总分", -1)),
                _cache[12] || (_cache[12] = _createElementVNode("th", null, "班次", -1)),
                _cache[13] || (_cache[13] = _createElementVNode("th", null, "段次", -1)),
                _cache[14] || (_cache[14] = _createElementVNode("th", null, "语文", -1)),
                _cache[15] || (_cache[15] = _createElementVNode("th", null, "语文班次", -1)),
                _cache[16] || (_cache[16] = _createElementVNode("th", null, "语文段次", -1)),
                _cache[17] || (_cache[17] = _createElementVNode("th", null, "数学", -1)),
                _cache[18] || (_cache[18] = _createElementVNode("th", null, "数学班次", -1)),
                _cache[19] || (_cache[19] = _createElementVNode("th", null, "数学段次", -1)),
                _cache[20] || (_cache[20] = _createElementVNode("th", null, "外语", -1)),
                _cache[21] || (_cache[21] = _createElementVNode("th", null, "外语班次", -1)),
                _cache[22] || (_cache[22] = _createElementVNode("th", null, "外语段次", -1)),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dynamicSubjects.value, (subject) => {
                  return (_openBlock(), _createElementBlock(_Fragment, {
                    key: subject.name
                  }, [
                    _createElementVNode("th", null, _toDisplayString(subject.name), 1),
                    _createElementVNode("th", null, _toDisplayString(subject.name) + "班次", 1),
                    _createElementVNode("th", null, _toDisplayString(subject.name) + "段次", 1)
                  ], 64))
                }), 128))
              ])
            ]),
            _createElementVNode("tbody", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayedStudents.value, (student, index) => {
                return (_openBlock(), _createElementBlock("tr", {
                  class: _normalizeClass({ 
              'highlight-row': student.studentId === currentStudentId.value, 
              'third-place-divider': index === 2 
            }),
                  key: student.studentId
                }, [
                  _createElementVNode("td", null, _toDisplayString(student.name), 1),
                  _createElementVNode("td", null, _toDisplayString(student.totalScore), 1),
                  _createElementVNode("td", null, _toDisplayString(student.classRank), 1),
                  _createElementVNode("td", null, _toDisplayString(student.overallRank), 1),
                  _createElementVNode("td", null, _toDisplayString(student.chineseScore), 1),
                  _createElementVNode("td", null, _toDisplayString(student.chineseClassRank), 1),
                  _createElementVNode("td", null, _toDisplayString(student.chineseOverallRank), 1),
                  _createElementVNode("td", null, _toDisplayString(student.mathScore), 1),
                  _createElementVNode("td", null, _toDisplayString(student.mathClassRank), 1),
                  _createElementVNode("td", null, _toDisplayString(student.mathOverallRank), 1),
                  _createElementVNode("td", null, _toDisplayString(student.englishScore), 1),
                  _createElementVNode("td", null, _toDisplayString(student.englishClassRank), 1),
                  _createElementVNode("td", null, _toDisplayString(student.englishOverallRank), 1),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dynamicSubjects.value, (subject) => {
                    return (_openBlock(), _createElementBlock(_Fragment, {
                      key: subject.name
                    }, [
                      _createElementVNode("td", null, _toDisplayString(student[`${subject.key}Score`]), 1),
                      _createElementVNode("td", null, _toDisplayString(student[`${subject.key}ClassRank`]), 1),
                      _createElementVNode("td", null, _toDisplayString(student[`${subject.key}OverallRank`]), 1)
                    ], 64))
                  }), 128))
                ], 2))
              }), 128))
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})